import React, { useMemo, useRef, useState } from "react";

// ✅ shared UI lives under src/components
import CountsTable from "../../components/CountsTable";
import ExportMenu from "../../components/ExportMenu";

// ✅ charts live under src/components/charts
import TrafficChart from "../../components/charts/TrafficChart";
import ChartExportMenu from "../../components/charts/ChartExportMenu";

// ✅ shared libs live under src/lib
import { ymd, parseHour12 } from "../../lib/dates";
import { goSightings } from "../../lib/links";
import { toQueryCam } from "../../lib/cameras";
import { exportTable } from "../../lib/csv";

const navBtn = {
  padding: "4px 10px",
  borderRadius: 8,
  border: "1px solid #1f2834",
  background: "#151b24",
  color: "white",
  lineHeight: 1,
  fontSize: 16,
  cursor: "pointer",
};

export default function TodayCard({ day, setDay, visible, rows, plate }) {
  const [stacked, setStacked] = useState(true);
  const chartRef = useRef(null);

  const card = { background: "#0f141c", border: "1px solid #1f2834", borderRadius: 16, padding: 16 };

  const chartData = useMemo(() => {
    const src = Array.isArray(rows) ? rows : [];
    return src.map((r) => ({
      label: r.time,
      k41: Number(r["41"] || 0),
      chel: Number(r["Cheltenham"] || 0),
      pool: Number(r["Pool"] || 0),
    }));
  }, [rows]);

  const visibleRowTotalFor = (r) =>
    (visible.cam41 ? Number(r["41"] ?? 0) : 0) +
    (visible.chel ? Number(r["Cheltenham"] ?? 0) : 0) +
    (visible.pool ? Number(r["Pool"] ?? 0) : 0);

  const buildExport = () => {
    const src = (rows || []).filter((r) => visibleRowTotalFor(r) > 0);
    const headers = [
      "time",
      ...(visible.cam41 ? ["41"] : []),
      ...(visible.chel ? ["Cheltenham"] : []),
      ...(visible.pool ? ["Pool"] : []),
      "Total",
    ];
    const out = src.map((r) => ({
      time: r.time,
      ...(visible.cam41 ? { "41": r["41"] } : {}),
      ...(visible.chel ? { Cheltenham: r["Cheltenham"] } : {}),
      ...(visible.pool ? { Pool: r["Pool"] } : {}),
      Total: visibleRowTotalFor(r),
    }));
    return { headers, rows: out, title: `Traffic on ${ymd(day)}`, filename: `today_${ymd(day)}` };
  };

  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, color: "#9ec3ff", fontSize: 18 }}>Traffic on {ymd(day)}</h2>
        <ExportMenu
          onExport={(t) => {
            const { headers, rows, title, filename } = buildExport();
            exportTable(t, headers, rows, title, filename);
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Day</div>
        <button
          style={navBtn}
          aria-label="Previous day"
          onClick={() => setDay(new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1))}
        >
          ‹
        </button>
        <input
          type="date"
          value={ymd(day)}
          onChange={(e) => setDay(new Date(e.target.value))}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #1f2834", background: "#0f141c", color: "white" }}
        />
        <button
          style={navBtn}
          aria-label="Next day"
          onClick={() => setDay(new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1))}
        >
          ›
        </button>
        <button
          onClick={() => setDay(new Date())}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #1f2834", background: "#151b24", color: "white" }}
        >
          Today
        </button>
      </div>

      <CountsTable
        rows={rows}
        firstColKey="time"
        firstColHeader="Time / Camera"
        visible={visible}
        onFirstCol={() => {}}
        onCell={(r, key) => {
          const hour = parseHour12(r.time);
          const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0);
          const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0, 0);
          const cams =
            key === "Total"
              ? [
                  ...(visible.cam41 ? ["41"] : []),
                  ...(visible.chel ? ["cheltenham"] : []),
                  ...(visible.pool ? ["pool"] : []),
                ]
              : [toQueryCam(key)];
          goSightings(start, end, cams, plate);
        }}
      />

      {/* Chart header: stacked/grouped + export */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Chart</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={stacked ? "stacked" : "grouped"}
            onChange={(e) => setStacked(e.target.value === "stacked")}
            style={{ padding: "4px 8px", background: "#0f141c", color: "white", borderRadius: 6, border: "1px solid #1f2834" }}
          >
            <option value="stacked">Stacked</option>
            <option value="grouped">Grouped</option>
          </select>
          <ChartExportMenu targetRef={chartRef} filename={`today_${ymd(day)}`} />
        </div>
      </div>

      <div ref={chartRef}>
        <TrafficChart data={chartData} visible={visible} stacked={stacked} height={160} />
      </div>
    </section>
  );
}
